/**
 * Filter Builder Component
 * Visual interface for building member group filters
 */

import React, { useState } from 'react';
import { Filter } from '@/services/groupsApi';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface FilterBuilderProps {
  filters: Filter[];
  onChange: (filters: Filter[]) => void;
  memberCount?: number;
  onPreview?: () => void;
}

const OPERATORS = [
  { value: 'EQ', label: 'Equals' },
  { value: 'NE', label: 'Not Equals' },
  { value: 'LT', label: 'Less Than' },
  { value: 'GT', label: 'Greater Than' },
  { value: 'LE', label: 'Less Than or Equal' },
  { value: 'GE', label: 'Greater Than or Equal' },
  { value: 'IN', label: 'In List' },
  { value: 'NOT_IN', label: 'Not In List' },
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'STARTS_WITH', label: 'Starts With' },
  { value: 'ENDS_WITH', label: 'Ends With' },
] as const;

const FIELD_OPTIONS = [
  // Migrated / explicit list (used for legacy migrations where the historical
  // group membership is stored as an explicit list of member IDs).
  { value: 'id', label: 'Member ID (explicit list)', type: 'number', category: 'Migrated' },

  // Demographics
  { value: 'gender', label: 'Gender', type: 'select', category: 'Demographics', options: [
    { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-Binary' }, { value: 'transgender_male', label: 'Transgender Male' },
    { value: 'transgender_female', label: 'Transgender Female' }, { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
  ]},
  { value: 'race', label: 'Race', type: 'select', category: 'Demographics', options: [
    { value: 'white', label: 'White' }, { value: 'black', label: 'Black or African American' },
    { value: 'asian', label: 'Asian' }, { value: 'hispanic', label: 'Hispanic or Latino' },
    { value: 'native_american', label: 'American Indian / Alaska Native' },
    { value: 'pacific_islander', label: 'Native Hawaiian / Pacific Islander' },
    { value: 'two_or_more', label: 'Two or More Races' }, { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
  ]},
  { value: 'ethnicity', label: 'Ethnicity', type: 'select', category: 'Demographics', options: [
    { value: 'hispanic', label: 'Hispanic or Latino' }, { value: 'not_hispanic', label: 'Not Hispanic or Latino' },
    { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
  ]},
  { value: 'age', label: 'Age', type: 'number', category: 'Demographics' },
  { value: 'dob', label: 'Date of Birth', type: 'date', category: 'Demographics' },

  // Contact Information
  { value: 'language', label: 'Preferred Language', type: 'select', category: 'Contact', options: [
    { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' },
    { value: 'zh', label: 'Chinese' }, { value: 'vi', label: 'Vietnamese' },
    { value: 'ko', label: 'Korean' }, { value: 'tl', label: 'Tagalog' },
    { value: 'ar', label: 'Arabic' }, { value: 'fa', label: 'Farsi' },
    { value: 'hy', label: 'Armenian' }, { value: 'ru', label: 'Russian' },
    { value: 'other', label: 'Other' },
  ]},
  { value: 'email', label: 'Email', type: 'text', category: 'Contact' },
  { value: 'phone_no', label: 'Phone Number', type: 'text', category: 'Contact' },
  { value: 'has_email', label: 'Has Email', type: 'boolean', category: 'Contact' },
  { value: 'has_phone', label: 'Has Phone', type: 'boolean', category: 'Contact' },

  // Location
  { value: 'zip_code', label: 'Zip Code', type: 'text', category: 'Location' },
  { value: 'city', label: 'City', type: 'text', category: 'Location' },
  { value: 'state', label: 'State', type: 'text', category: 'Location' },
  { value: 'county', label: 'County', type: 'text', category: 'Location' },

  // Enrollment & Programs
  { value: 'status', label: 'Status', type: 'select', category: 'Programs', options: [
    { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }, { value: 'discharged', label: 'Discharged' },
    { value: 'closed', label: 'Closed' },
  ]},
  { value: 'program_id', label: 'Program ID', type: 'number', category: 'Programs' },
  { value: 'enrollment_date', label: 'Enrollment Date', type: 'date', category: 'Programs' },
  { value: 'program_status', label: 'Program Status', type: 'select', category: 'Programs', options: [
    { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }, { value: 'completed', label: 'Completed' },
    { value: 'withdrawn', label: 'Withdrawn' },
  ]},

  // Health & Risk
  { value: 'risk_score', label: 'Risk Score', type: 'number', category: 'Health' },
  { value: 'chronic_conditions', label: 'Chronic Conditions', type: 'text', category: 'Health' },
  { value: 'last_visit_date', label: 'Last Visit Date', type: 'date', category: 'Health' },
  { value: 'insurance_type', label: 'Insurance Type', type: 'select', category: 'Health', options: [
    { value: 'medicaid', label: 'Medicaid' }, { value: 'medicare', label: 'Medicare' },
    { value: 'commercial', label: 'Commercial' }, { value: 'dual_eligible', label: 'Dual Eligible' },
    { value: 'uninsured', label: 'Uninsured' }, { value: 'other', label: 'Other' },
  ]},
  // LOB / Product — backed by member_plan_enrollments via a correlated
  // EXISTS sub-query in groups.build_filter_sql. Options are loaded
  // dynamically from /api/orgs/{cbo_id}/product-lines and resolved by id.
  { value: 'lob_id',     label: 'Line of Business', type: 'select', category: 'Plan / LOB' } as any,
  { value: 'product_id', label: 'Plan / Product',   type: 'select', category: 'Plan / LOB' } as any,

  // Member Attributes
  { value: 'patient_no', label: 'Patient Number', type: 'text', category: 'Attributes' },
  { value: 'tags', label: 'Tags', type: 'text', category: 'Attributes' },
  { value: 'created_at', label: 'Created Date', type: 'date', category: 'Attributes' },
  { value: 'updated_at', label: 'Last Updated', type: 'date', category: 'Attributes' },
];

interface DynamicOption { value: number; label: string }

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  filters,
  onChange,
  memberCount,
  onPreview,
}) => {
  const { t } = useAppTranslation();

  // Dynamic options for lob_id and product_id, loaded once per mount from
  // /api/orgs/{cbo_id}/product-lines. Empty for orgs without a seeded health plan.
  const [planOpts, setPlanOpts] = React.useState<{ lob_id: DynamicOption[]; product_id: DynamicOption[] }>({ lob_id: [], product_id: [] });
  React.useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    if (!cboId) return;
    fetch(`/api/orgs/${cboId}/product-lines`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
        'X-CBO-ID': cboId,
      },
    })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => {
        const lob: DynamicOption[] = (Array.isArray(rows) ? rows : []).map(r => ({ value: r.lob_id, label: r.lob_name }));
        const prod: DynamicOption[] = (Array.isArray(rows) ? rows : []).flatMap(r =>
          (r.products || []).map((p: any) => ({ value: p.id, label: `${p.name} (${r.lob_name})` }))
        );
        setPlanOpts({ lob_id: lob, product_id: prod });
      })
      .catch(() => setPlanOpts({ lob_id: [], product_id: [] }));
  }, []);

  const addFilter = () => {
    const newFilter: Filter = {
      field: 'status',
      operator: 'EQ',
      value: '',
      logic_operator: 'AND',
    };

    onChange([...filters, newFilter]);
  };

  const updateFilter = (index: number, updates: Partial<Filter>) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], ...updates };
    onChange(newFilters);
  };

  const removeFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    onChange(newFilters);
  };

  const getValueInput = (filter: Filter, index: number) => {
    const fieldOption = FIELD_OPTIONS.find((f) => f.value === filter.field) as any;
    const fieldType: string = fieldOption?.type || 'text';
    // Hard-coded options on the field win; otherwise fall back to dynamic
    // plan-line options keyed by field name (lob_id / product_id).
    const dynamicForField = (planOpts as any)[filter.field] as DynamicOption[] | undefined;
    const fieldOptions: { value: string | number; label: string }[] | undefined =
      fieldOption?.options || (dynamicForField && dynamicForField.length ? dynamicForField : undefined);

    const selectClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-800";
    const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm";

    // For IN and NOT_IN operators with predefined options, show multi-select checkboxes
    if ((filter.operator === 'IN' || filter.operator === 'NOT_IN') && fieldOptions) {
      const selected = Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : []);
      return (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-2 max-h-36 overflow-y-auto">
          {fieldOptions.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={(e) => {
                  const newValues = e.target.checked
                    ? [...selected, opt.value]
                    : selected.filter((v: string) => v !== opt.value);
                  updateFilter(index, { value: newValues });
                }}
                className="rounded border-gray-300"
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }

    // For IN and NOT_IN operators without predefined options, use comma-separated input
    if (filter.operator === 'IN' || filter.operator === 'NOT_IN') {
      return (
        <input
          type="text"
          value={Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
          onChange={(e) => {
            const values = e.target.value.split(',').map((v) => v.trim());
            updateFilter(index, { value: values });
          }}
          placeholder="value1, value2, value3"
          className={inputClass}
        />
      );
    }

    // Boolean fields
    if (fieldType === 'boolean') {
      return (
        <select
          value={filter.value?.toString() || 'true'}
          onChange={(e) => updateFilter(index, { value: e.target.value === 'true' })}
          className={selectClass}
        >
          <option value="true">{t('groups.yes', 'Yes')}</option>
          <option value="false">{t('groups.no', 'No')}</option>
        </select>
      );
    }

    // Select fields with predefined options
    if (fieldType === 'select' && fieldOptions) {
      return (
        <select
          value={filter.value || ''}
          onChange={(e) => updateFilter(index, { value: e.target.value })}
          className={selectClass}
        >
          <option value="">{t('groups.select_value', '— Select —')}</option>
          {fieldOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    // Standard input based on field type
    return (
      <input
        type={fieldType === 'select' ? 'text' : fieldType}
        value={filter.value}
        onChange={(e) => updateFilter(index, { value: e.target.value })}
        placeholder={t('groups.enter_value', 'Enter value')}
        className={inputClass}
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('groups.filters', 'Filters')}</h3>
          <p className="text-sm text-gray-600 mt-1">{t('groups.define_rules_to_dynamically_include', 'Define rules to dynamically include members')}</p>
        </div>
        {memberCount !== undefined && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">{t('groups.matching_members', 'Matching members:')}</span>
            <span className="text-2xl font-bold text-blue-600">{memberCount.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Filters */}
      {filters.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">{t('groups.no_filters_yet', 'No filters yet')}</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('groups.add_filters_to_define_your', 'Add filters to define your member group.')}</p>
          <button
            onClick={addFilter}
            className="mt-4 bg-v2accent hover:bg-v2accent/90 text-white px-4 py-2 rounded-lg text-sm"
          >
            {t('groups.add_first_filter', 'Add First Filter')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filters.map((filter, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="space-y-3">
                {/* First Row: Logic Operator (if needed) and Field */}
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <select
                      value={filter.logic_operator}
                      onChange={(e) => updateFilter(index, { logic_operator: e.target.value as 'AND' | 'OR' })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-sm font-medium w-24 flex-shrink-0"
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  )}

                  {/* Field */}
                  <select
                    value={filter.field}
                    onChange={(e) => updateFilter(index, { field: e.target.value })}
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-sm"
                  >
                    {['Demographics', 'Contact', 'Location', 'Programs', 'Health', 'Attributes'].map((category) => (
                      <optgroup key={category} label={category}>
                        {FIELD_OPTIONS.filter((opt) => opt.category === category).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Second Row: Operator and Value */}
                <div className="flex items-start gap-2">
                  {/* Operator */}
                  <select
                    value={filter.operator}
                    onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
                    className="w-40 flex-shrink-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-sm"
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  {/* Value */}
                  <div className="flex-1 min-w-0">{getValueInput(filter, index)}</div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFilter(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                    title={t('groups.remove_filter', 'Remove filter')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Add Filter Button */}
          <button
            onClick={addFilter}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 rounded-lg p-4 text-blue-600 hover:text-blue-700 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('groups.add_filter', 'Add Filter')}
          </button>
        </div>
      )}

      {/* Preview Button */}
      {onPreview && filters.length > 0 && (
        <button
          onClick={onPreview}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          {t('groups.preview_matching_members', 'Preview Matching Members')}
        </button>
      )}
    </div>
  );
};
